/* eslint-disable @typescript-eslint/no-explicit-any , complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { FC, ReactElement, ReactNode, useEffect, useState } from 'react';

import { formatCurrency, formatDecimal, FormattedMessage, useIntl } from '@futrli/localisation';
import { CHART_COLORS } from '@futrli/colors';
import { PerformanceMetricsCashflowTopCashItem } from '@futrli-sdk/predict';

import { UIBarChart } from '../../../../ui/charts/bar-chart/bar-chart';
import { PERIODS, PeriodDropdown } from './period-dropdown';

import parentStyles from '../performance.scss';
import styles from './table-with-bar-chart-card.scss';

interface Props {
  type?: 'in' | 'out';
  currency: string;
  chartData: { value: number; label: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableData: PerformanceMetricsCashflowTopCashItem[] | any[];
  dataLoading?: boolean;
  headers: ReactNode[];
  testId?: string;
  cardTitle: ReactElement;
  loadingRowsNumber?: number;
  noDataMessage?: ReactElement;
  onPeriodChange: (p: PERIODS) => void;
  customColors?: string | string[];
  customKeys?: string[];
  defaultPeriod?: PERIODS;
}

export const TableWithBarChartCard: FC<Props> = ({
  type,
  currency,
  tableData,
  chartData,
  dataLoading = false,
  headers,
  testId,
  cardTitle,
  loadingRowsNumber = 10,
  noDataMessage,
  onPeriodChange,
  customColors,
  customKeys = ['name', 'amount', 'percent_of_cash'],
  defaultPeriod = PERIODS.THIS_MONTH
}: Props) => {
  const [period, setPeriod] = useState<PERIODS>(defaultPeriod);
  const intl = useIntl();

  useEffect(() => setPeriod(defaultPeriod), [defaultPeriod]);

  const handleOnChange = (selectedPeriod: PERIODS) => {
    setPeriod(selectedPeriod);
    onPeriodChange(selectedPeriod);
  };

  return (
    <section data-testid={testId || 'table-with-bar-chart'} className={clsx(parentStyles.card)}>
      <h3 className={clsx(parentStyles.cardTitle, styles.title, parentStyles.noSubTitle)}>{cardTitle}</h3>
      <div className={clsx(styles.periodSelector)}>
        <PeriodDropdown period={period} disabled={dataLoading} onChange={handleOnChange} />
      </div>

      {tableData.length || dataLoading ? (
        <div className={clsx(styles.flexWrapper)}>
          <div className={clsx(styles.content)}>
            <table className={clsx(styles.table)}>
              <thead>
                <tr>
                  {headers.map((h, index) => (
                    <th key={index} className={clsx('uiTypeLabel')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              {dataLoading ? (
                <tbody className={clsx(styles.isLoading)}>
                  {Array.from({ length: loadingRowsNumber }, (_, i) => i).map(e => (
                    <tr key={e} data-testid="loading-row">
                      <td colSpan={3} />
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  {(tableData as any[]).map((row: any, index: number) => (
                    <tr key={`${period}-${index}`} data-testid="data-row">
                      <td className={clsx(styles.name, 'uiTypeFont12Regular')}>{row[customKeys[0]]}</td>
                      <td className={clsx('uiTypeFont16Regular')}>
                        {formatCurrency(+row[customKeys[1]], intl, currency, 0, 0)}
                      </td>
                      <td className={clsx('uiTypeFont16Regular')}>
                        {formatDecimal(row[customKeys[2]] * 100, intl, 0, 0)}
                        {'%'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          <div className={clsx(styles.chartSection)}>
            <div className={clsx(styles.chartWrapper)}>
              <UIBarChart
                labels={chartData.map((_, i) => tableData[i][customKeys[0]] || '')}
                datasets={[
                  {
                    data: chartData.map(e => e.value),
                    colors: customColors || CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].PRIMARY
                  }
                ]}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={clsx(styles.noDataWrapper)}>
          <p className={clsx('uiTypeFont14RegularLight', styles.noData)}>
            {noDataMessage || (
              <FormattedMessage id="predict.no_data_for_time_period" defaultMessage="No data for this time period." />
            )}
          </p>
        </div>
      )}
    </section>
  );
};
